// css =>
import "../../BaseStructure.css"
import Styled from "./EditProfile.module.css";

// componenets =>
import Header from "../../Components/header/header.comp";
import MenuBar from "../../Components/menuBar/menuBar.comp";
import InputBox from "../../Components/inputBox/inputBox.comp";
import SelectBox from "../../Components/selectBox/selectBox.comp";
import InputSpace from "../../Components/inputsSpace/inputsSpace.comp";
import LocationShow from "../../Components/locationShow/locationShow.comp";
import FirstMenuBar from "../../Components/firstMenuBar/firstMenuBar.comp";
import MobileMenuBar from "../../Components/mobileMenuBar/mobileMenuBar.comp";

// react =>
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

// API =>
import backEnd from "../../services/Api";
import { useUserStore } from "../../hooks/useUserStore";

// Type =>
import { USER_PROFILE_TYPE } from "./EditProfileType";

// utils =>
import { LocalCSRFReceiver } from "../../utils/GetLocalCSRF";

// context =>
import { useMessage } from "../../Contexts/MessageBoxContext";

// service =>
import { citiesOption, provinceOptions } from "./EditProfileService";

function EditProfile() {

    const navigate = useNavigate();
    const { showMessage } = useMessage();

    const nameInputRef = useRef<HTMLInputElement>(null);
    const lastNameInputRef = useRef<HTMLInputElement>(null);
    const phoneNumberInputRef = useRef<HTMLInputElement>(null);
    const addressInputRef = useRef<HTMLInputElement>(null);
    const postalCodeInputRef = useRef<HTMLInputElement>(null);
    const emailAddressInputRef = useRef<HTMLInputElement>(null);

    const citySelectBoxRef = useRef<HTMLSelectElement>(null);
    const provinceSelectBoxRef = useRef<HTMLSelectElement>(null);
    
    const [city, setCity] = useState("");
    const [province, setProvince] = useState("تهران");
    const [alreadyUserInfo, setAlreadyUserInfo] = useState<USER_PROFILE_TYPE>();

    useEffect(() => {
        document.title = "ویرایش پروفایل | ویدوک";

        if (!useUserStore.getState().user?.id) return;

        backEnd.get(`users/profile-get/${ useUserStore.getState().user?.id }/`).then(( result ) => {
            setAlreadyUserInfo(result.data);
        }).catch(( error ) => {
            const status = error.response?.status || 404;
            let message = "خطای غیرمنتظره‌ای رخ داد!";

            if (error.response) {
                switch (status) {
                    case 400:
                        message = "درخواست شما معتبر نیست.";
                    break;

                    case 401:
                    case 403:
                    case 404:
                        useUserStore.getState().setUser(null);
                        localStorage.removeItem("user-storage");

                        message = "لطفاً ابتدا وارد حساب کاربری خود شوید یا ثبت نام کنید!";
                        navigate("/sign-in");
                    break;

                    case 500:
                        message = "خطای سرور، لطفاً بعداً امتحان کنید.";
                    break;

                    default:
                        message = error.response.data?.detail || message;
                }
            }
            
            else if (error.request) {
                message = "ارتباط با سرور برقرار نشد. اینترنت خود را بررسی کنید.";
            }

            else {
                message = "در ارسال درخواست مشکلی به وجود آمد.";
            }

            showMessage("error", message);
        })
    }, []);

    const handleInfoChange = async () => {
        const changedUserData: USER_PROFILE_TYPE = {
            id: alreadyUserInfo!.id,
            name: nameInputRef.current?.value || alreadyUserInfo!.name,
            last_name: lastNameInputRef.current?.value || alreadyUserInfo!.last_name,
            email_address: emailAddressInputRef.current?.value || alreadyUserInfo!.email_address,
            postal_code: postalCodeInputRef.current?.value || alreadyUserInfo!.postal_code,
            receiving_province: provinceSelectBoxRef.current?.value || alreadyUserInfo!.receiving_province,
            receiving_city: citySelectBoxRef.current?.value || alreadyUserInfo!.receiving_city,
            address: addressInputRef.current?.value || alreadyUserInfo!.address,
        };

        const csrf = await LocalCSRFReceiver();

        backEnd.put(
            `/users/profile-update/${ useUserStore.getState().user?.id }/`,
            {
                name: changedUserData.name,
                last_name: changedUserData.last_name,
                email_address: changedUserData.email_address,
                postal_code: changedUserData.postal_code,
                receiving_province: changedUserData.receiving_province,
                receiving_city: changedUserData.receiving_city,
                address: changedUserData.address,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrf,
                }
            }

        ).then(( result ) => {
            showMessage("success", "پروفایل با موفقیت بروزرسانی شد");
        }).catch(( error ) => {
            let message = "خطای غیرمنتظره‌ای رخ داد!";

            if (error.response) {
                const status = error.response.status;

                switch (status) {
                    case 400:
                        message = "اطلاعاتی که وارد کردید معتبر نیست. لطفاً دوباره بررسی کنید.";
                        break;

                    case 401:
                    case 403:
                        useUserStore.getState().setUser(null);
                        localStorage.removeItem("user-storage");

                        message = "لطفاً ابتدا وارد حساب کاربری خود شوید یا ثبت نام کنید!";
                        navigate("/sign-in");
                        break;

                    case 404:
                        message = "پروفایل موردنظر یافت نشد!";
                        break;

                    case 500:
                        message = "مشکلی در سرور رخ داده، لطفاً لحظاتی بعد دوباره تلاش کنید.";
                        break;

                    default:
                        message = error.response.data?.detail || message;
                }
            }

            else if (error.request) {
                message = "ارتباط با سرور برقرار نشد. اینترنت خود را بررسی کنید.";
            } else {
                message = "در ارسال درخواست مشکلی به وجود آمد.";
            }

            showMessage("error", message);
        });
    }

    return(
        <>
            <Header />
            <MobileMenuBar key={ 0 } isAuthenticated />
            <LocationShow key={ 0 } pathName="حساب کاربری من" />
            <FirstMenuBar />

            <div className="row" data-aos="fade-up" id={ Styled.EditProfileForm }>
                <InputBox inputType="char" inputTittle="نام" ref={ nameInputRef } defaultValue={ alreadyUserInfo?.name } placeHolder="ثبت نشده!" />
                <InputBox inputType="char" inputTittle="نام خانوادگی" ref={ lastNameInputRef } defaultValue={ alreadyUserInfo?.last_name } placeHolder="ثبت نشده!" />

                <InputSpace />

                <InputBox inputType="char" inputTittle="تلفن همراه" ref={ phoneNumberInputRef } value={ alreadyUserInfo?.phone_number } isDisable={ true } />
                <InputBox inputType="char" inputTittle="کد پستی" ref={ postalCodeInputRef } defaultValue={ alreadyUserInfo?.postal_code } placeHolder="ثبت نشده!" />

                <InputSpace />

                <InputBox inputType="char" inputTittle="عنوان آدرس" ref={ addressInputRef } defaultValue={ alreadyUserInfo?.address_title || "" } placeHolder="ثبت نشده!" />
                <InputBox inputTittle="پلاک" inputType="number" ref={ addressInputRef } defaultValue={ alreadyUserInfo?.house_number || "" } placeHolder="ثبت نشده!" />

                <InputSpace />

                <SelectBox
                    key={ 0 }
                    ref={ provinceSelectBoxRef }
                    title="استان"
                    value={ province }
                    options={ provinceOptions }
                    onChange={(value) => {
                        setProvince(value);
                        setCity("");
                    }}
                />
                <SelectBox
                    key={ 0 }
                    ref={ citySelectBoxRef }
                    title="شهر"
                    value={ city }
                    options={ province ? citiesOption[province] : [] }
                    onChange={(value) => setCity(value)}
                />

                <InputSpace />

                <InputBox inputType="text" inputTittle="آدرس" ref={ addressInputRef } defaultValue={ alreadyUserInfo?.address } placeHolder="ثبت نشده!" />

                <InputSpace />

                <InputBox inputType="text" inputTittle="ایمیل" ref={ emailAddressInputRef } defaultValue={ alreadyUserInfo?.email_address } placeHolder="ثبت نشده!" />
            </div>

            <div className="row" data-aos="fade-up">
                <div className="col-12">
                    <button id={ Styled.EditProfileButton } onClick={ handleInfoChange }>
                        <h3>ویرایش مشخصات</h3>
                    </button>
                </div>
            </div>

            <br /><br /><br />

            <MenuBar />

            <br /><br /><br />
        </>
    );
}

export default EditProfile;
